<template>
  <div
    id="expanded_sidebar"
    class="flex flex-col w-full h-full justify-between"
  >
    <div class="flex flex-col gap-1">
      <template v-for="option in options" :key="option.id">
        <router-link
          v-if="
            !option.sections &&
            !option.event &&
            option.alignment === sidebarOptionPosition.TOP
          "
          :to="option.route || ''"
          :class="{ 'pointer-events-none': !option.route }"
        >
          <SidebarItem
            :option="option"
            :is-expanded="true"
            :is-selected="isOptionSelected(option)"
            :is-disabled="!option.route"
          />
        </router-link>

        <div
          v-else-if="
            option.event && option.alignment === sidebarOptionPosition.TOP
          "
          @click="$emit('event-click', option.id)"
          class="cursor-pointer"
        >
          <SidebarItem
            :option="option"
            :is-expanded="true"
            :is-selected="false"
            :is-disabled="false"
          />
        </div>

        <router-link
          v-else-if="option.sections && isDirectLinkApp(option.id, option.sections?.some(s => s.externalUrl))"
          :to="option.route || option.sections?.[0]?.route || ''"
          :class="{ 'pointer-events-none': !(option.route || option.sections?.[0]?.route) }"
        >
          <div class="group">
            <SidebarItem
              :option="option"
              :is-expanded="true"
              :is-selected="isOptionSelected(option)"
              :is-disabled="!(option.route || option.sections?.[0]?.route)"
              :app-id="option.sections?.[0]?.appId"
              :app-name="option.sections?.[0]?.appName"
              dropdown-placement="bottomLeft"
              @dropdown-open-change="
                (open) => handleDropdownOpenChange(open, option.id)
              "
              @dropdown-mode-change="
                (mode) => handleModeChange(mode, option.id)
              "
            />
          </div>
        </router-link>

        <div
          v-else-if="option.sections"
          :class="[
            'transition-all duration-300 ease-in-out',
            {
              'flex flex-col bg_white_4 pb-2 gap-1':
                activeSection === option.id,
            },
          ]"
        >
          <div
            @click="emit('section-click', option.id)"
            :class="[
              'sidebar_item justify-between p-[10px] group cursor-pointer',
              {
                sidebar_item_selected:
                  isOptionSelected(option) && activeSection !== option.id,
              },
            ]"
          >
            <div class="flex flex-row gap-2 items-center">
              <ThemeImage
                class="size-5"
                :icon="
                  isOptionSelected(option) ? option.selected : option.unselected
                "
                :alt="`${option.text.toLowerCase()}_icon`"
              />
              <span
                :class="[
                  'text-sm font-normal whitespace-nowrap',
                  { 'opacity-80': !isOptionSelected(option) },
                ]"
              >
                {{ option.text }}
              </span>
            </div>

            <div class="flex items-center gap-1">
              <div
                v-if="option.id === 'projects' && (hasUnreadIndicator || totalUnreadCount > 0) && activeSection !== option.id && chatsUnreadDisplayCount > 0"
                class="flex items-center justify-center text-xs text-white font-semibold pt-[1px] px-[4px] leading-[14px] !min-w-[16px] count_tag"
              >
                {{ chatsUnreadDisplayCount }}
              </div>
              <ThemeImage
                class="size-4 arrow_icon transition-transform duration-300"
                :class="{ 'rotate-90': activeSection === option.id }"
                :icon="imageNames.RIGHT_ARROW_SMALL_GREY"
                alt="right_arrow_small"
              />
            </div>
          </div>

          <div class="sections_container overflow-hidden">
            <transition
              @enter="startEnterTransition"
              @leave="startLeaveTransition"
              @after-enter="endTransition"
              @after-leave="endTransition"
            >
              <div
                v-show="activeSection === option.id"
                class="sections_wrapper flex flex-col gap-1 pl-6 pr-2"
              >
                <template v-for="section in option.sections" :key="section.id">
                  <!-- Section with nested sub-objects (sub_sections) -->
                  <div
                    v-if="section?.sub_sections?.length"
                    class="flex flex-col gap-1"
                  >
                    <!-- CRM parent section with route - clickable and toggleable -->
                    <router-link
                      v-if="section.route && section.route.includes('/crm/')"
                      :to="section.route"
                      custom
                      v-slot="{ navigate, href }"
                    >
                      <a
                        :href="href"
                        @click="
                          (e) =>
                            handleParentSectionClick(e, navigate, section.id)
                        "
                        class="group cursor-pointer block w-full"
                        style="text-decoration: none; color: inherit"
                      >
                        <SidebarItem
                          :option="section"
                          :is-expanded="true"
                          :is-selected="isOptionSelected(section)"
                          :show-arrow="true"
                          :is-open="activeSubSection === section.id"
                          :hideIcon="true"
                          :app-id="section.appId"
                          :app-name="section.appName"
                          dropdown-placement="bottomLeft"
                          @dropdown-open-change="
                            (open) => handleDropdownOpenChange(open, section.id)
                          "
                          @dropdown-mode-change="
                            (mode) => handleModeChange(mode, section.id)
                          "
                        />
                      </a>
                    </router-link>

                    <!-- Non-CRM parent section - only toggleable -->
                    <div
                      v-else
                      @click="toggleSubSection(section.id)"
                      class="group cursor-pointer"
                    >
                      <SidebarItem
                        :option="section"
                        :is-expanded="true"
                        :is-selected="isOptionSelected(section)"
                        :show-arrow="true"
                        :is-open="activeSubSection === section.id"
                        :hideIcon="true"
                        :app-id="section.appId"
                        :app-name="section.appName"
                        dropdown-placement="bottomLeft"
                        @dropdown-open-change="
                          (open) => handleDropdownOpenChange(open, section.id)
                        "
                        @dropdown-mode-change="
                          (mode) => handleModeChange(mode, section.id)
                        "
                      />
                    </div>

                    <transition
                      @enter="startEnterTransition"
                      @leave="startLeaveTransition"
                      @after-enter="endTransition"
                      @after-leave="endTransition"
                    >
                      <div
                        v-show="activeSubSection === section.id"
                        class="flex flex-col gap-1 pl-6"
                      >
                        <SubSectionItem
                          v-for="item in section.sub_sections"
                          :key="item.id"
                          :item="item"
                          :depth="1"
                          :parent-route="section.route"
                        />
                      </div>
                    </transition>
                  </div>

                  <!-- Regular section without sub-objects -->
                  <a-tooltip
                    v-else-if="!section.route"
                    placement="right"
                    color="var(--tooltip-bg)"
                  >
                    <template #title>
                      <SidebarItem
                        :option="section"
                        :is-expanded="true"
                        :is-selected="isOptionSelected(section)"
                        :is-disabled="true"
                        :is-tooltip="true"
                        :hideIcon="true"
                      />
                    </template>

                    <SidebarItem
                      :option="section"
                      :is-expanded="true"
                      :is-selected="isOptionSelected(section)"
                      :is-disabled="true"
                      :hideIcon="true"
                    />
                  </a-tooltip>

                  <router-link v-else :to="section.route">
                    <div class="group">
                      <SidebarItem
                        :option="section"
                        :is-expanded="true"
                        :is-selected="isOptionSelected(section)"
                        :is-disabled="false"
                        :hideIcon="true"
                        :app-id="section.appId"
                        :app-name="section.appName"
                        :show-indicator="isChatsSection(section) && (hasUnreadIndicator || totalUnreadCount > 0)"
                        :unread-count="isChatsSection(section) ? chatsUnreadDisplayCount : 0"
                        dropdown-placement="bottomLeft"
                        @dropdown-open-change="
                          (open) => handleDropdownOpenChange(open, section.id)
                        "
                        @dropdown-mode-change="
                          (mode) => handleModeChange(mode, section.id)
                        "
                      />
                    </div>
                  </router-link>
                </template>
              </div>
            </transition>
          </div>
        </div>
      </template>
    </div>

    <div class="flex flex-col gap-1">
      <template
        v-for="option in options"
        :key="`${option?.id}_expanded_bottom_option`"
      >
        <router-link
          v-if="
            !option.event && option.alignment === sidebarOptionPosition.BOTTOM
          "
          :to="option.route || ''"
          :class="{ 'pointer-events-none': !option.route }"
        >
          <SidebarItem
            :option="option"
            :is-expanded="true"
            :is-selected="isOptionSelected(option)"
            :is-disabled="!option.route"
          />
        </router-link>

        <div
          v-else-if="
            option.event && option.alignment === sidebarOptionPosition.BOTTOM
          "
          @click="$emit('event-click', option.id)"
          class="cursor-pointer"
        >
          <SidebarItem
            :option="option"
            :is-expanded="true"
            :is-selected="false"
            :is-disabled="false"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { provide, computed, watch } from "vue";
import { useStore } from "vuex";
import SidebarItem from "./SidebarItem.vue";
import SubSectionItem from "./SubSectionItem.vue";
import { useTransition } from "@/src/composables/useTransition";
import { useSidebarLogic } from "@/src/composables/useSidebarLogic";
import "@/src/styles/transition.css";
import { sidebarOptionPosition } from "@/src/constants/sidebarConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { isDirectLinkApp } from "@/src/constants/appCodeConstants.js";
import { isChatsSection } from "@/src/utils/sidebarUtils.js";

const store = useStore();
const hasUnreadIndicator = computed(() => store.getters["chats/hasUnreadIndicator"]);
const totalUnreadCount = computed(() => store.getters["chats/totalUnreadCount"]);
const chatsUnreadDisplayCount = computed(() => {
  const count = totalUnreadCount.value || 0;
  if (count > 0) return count;
  return hasUnreadIndicator.value ? 1 : 0;
});

const { startEnterTransition, startLeaveTransition, endTransition } =
  useTransition();

const {
  activeSubSection,
  toggleSubSection,
  toggleNestedSubSection,
  getNestedSubSectionState,
  handleDropdownOpenChange,
  handleModeChange,
  isOptionSelected,
} = useSidebarLogic();

provide("toggleNestedSubSection", toggleNestedSubSection);
provide("getNestedSubSectionState", getNestedSubSectionState);
provide("isOptionSelected", isOptionSelected);

const props = defineProps({
  options: { type: Array, required: true },
  activeSection: { type: String, default: null },
});

const emit = defineEmits(["section-click", "event-click"]);


const handleParentSectionClick = (e, navigate, sectionId) => {
  const clickedElement = e.target;
  const isArrowImage =
    clickedElement.tagName === "IMG" &&
    clickedElement.alt === "right_arrow_small";
  const arrowContainer = clickedElement.closest(".size-4.transition-transform");
  const isArrowClick =
    isArrowImage || (arrowContainer && arrowContainer.contains(clickedElement));

  if (isArrowClick) {
    e.preventDefault();
    e.stopPropagation();
    toggleSubSection(sectionId);
    return;
  }

  toggleSubSection(sectionId);
  navigate(e);
};
</script>

<style scoped>
.arrow_icon,
.three_dot_icon {
  opacity: 0;
}
.sidebar_item:hover .arrow_icon,
.sidebar_item_selected:hover .arrow_icon,
.sidebar_item:hover .three_dot_icon,
.sidebar_item_selected:hover .three_dot_icon {
  opacity: 1;
}
.sections_container {
  position: relative;
}

.sections_wrapper {
  position: relative;
  transform-origin: top;
  will-change: max-height, opacity, transform;
}
</style>


